import React, { useState, useMemo } from 'react';
import TaxPricingRuleEditor from './TaxPricingRuleEditor';
import { BUSINESS_TYPES, TYPE_LABELS } from './businessRegistryUi';
import './AdminTaxRuleFormScreen.css';

// Tax Category Definitions Tailored for Properties (Households) vs Businesses (Commercial)
const TAX_CATEGORIES = {
  property: [
    {
      id: 'general_property_tax',
      label: '🏠 घरपट्टी / सामान्य इमारत कर (General Property Tax)',
      default_name_mr: 'इमारत व बिगरशेती जमीन कर (घरपट्टी)',
      code_prefix: 'PROP_TAX_GENERAL',
      account_class: 'property_tax',
      default_formula: 'property_class_rate',
      desc: 'इमारती, घरे, गाळे व जमिनीवरील मूलभूत वार्षिक कर',
    },
    {
      id: 'domestic_water_tax',
      label: '💧 घरगुती पाणीपट्टी (Domestic Piped Water Charges)',
      default_name_mr: 'घरगुती नळ पाणीपट्टी',
      code_prefix: 'WATER_TAX_DOMESTIC',
      account_class: 'service_charge',
      default_formula: 'fixed',
      desc: 'घरगुती वापरासाठी नळ पाणीपुरवठा सेवा शुल्क',
    },
    {
      id: 'street_light_tax',
      label: '💡 सार्वजनिक दिवाबत्ती कर (Street Lighting Tax)',
      default_name_mr: 'सार्वजनिक दिवाबत्ती कर',
      code_prefix: 'LIGHT_TAX_STREET',
      account_class: 'service_charge',
      default_formula: 'fixed',
      desc: 'सार्वजनिक रस्ते व गल्ल्यांमधील दिवाबत्ती देखभाल कर',
    },
    {
      id: 'health_sanitation_tax',
      label: '🏥 सार्वजनिक आरोग्य व स्वच्छता कर (Sanitation Tax)',
      default_name_mr: 'सार्वजनिक आरोग्य व स्वच्छता कर',
      code_prefix: 'HEALTH_SAN_TAX',
      account_class: 'service_charge',
      default_formula: 'fixed',
      desc: 'सार्वजनिक परिसर स्वच्छता, कीटकनाशक व आरोग्य उपकर',
    },
    {
      id: 'domestic_drainage_tax',
      label: '🚯 सांडपाणी व गटार कर (Domestic Drainage Cess)',
      default_name_mr: 'घरगुती सांडपाणी गटार कर',
      code_prefix: 'DRAINAGE_DOMESTIC_TAX',
      account_class: 'service_charge',
      default_formula: 'fixed',
      desc: 'घरगुती सांडपाणी व भूमिगत गटार व्यवस्थापन कर',
    },
    {
      id: 'solid_waste_fee',
      label: '🧹 घनकचरा संकलन शुल्क (Solid Waste Fee)',
      default_name_mr: 'घनकचरा संकलन सेवा शुल्क',
      code_prefix: 'WASTE_FEE_DOMESTIC',
      account_class: 'service_charge',
      default_formula: 'fixed',
      desc: 'घरोघरी कचरा संकलन व प्रक्रिया सेवा शुल्क',
    },
    {
      id: 'special_local_cess',
      label: '⚖️ इतर विशेष मालमत्ता उपकर (Special Property Cess)',
      default_name_mr: 'विशेष स्थानिक मालमत्ता उपकर',
      code_prefix: 'SPECIAL_PROP_CESS',
      account_class: 'service_charge',
      default_formula: 'fixed',
      desc: 'ग्रामसभा ठरावानुसार इतर विशेष सेवा उपकर किंवा फी',
    },
  ],
  business: [
    {
      id: 'trade_license_tax',
      label: '🏢 व्यापार परवाना व व्यवसाय कर (Trade License & Business Tax)',
      default_name_mr: 'अनुमत व्यापार व व्यवसाय कर',
      code_prefix: 'BIZ_TRADE_TAX',
      account_class: 'business_tax',
      default_formula: 'fixed',
      desc: 'दुकान, व्यवसाय व उद्योग सुरू ठेवण्यासाठी वार्षिक परवाना फी',
    },
    {
      id: 'commercial_water_tax',
      label: '💧 व्यावसायिक पाणीपट्टी (Commercial Water Charges)',
      default_name_mr: 'व्यावसायिक नळ पाणीपट्टी (हॉटेल्स, कारखाने इ.)',
      code_prefix: 'WATER_TAX_COMMERCIAL',
      account_class: 'service_charge',
      default_formula: 'fixed',
      desc: 'हॉटेल्स, कारखाने, गॅरेज, लॉन्ड्री इत्यादींसाठी विशेष व्यावसायिक पाणी दर',
    },
    {
      id: 'commercial_waste_sanitation_fee',
      label: '🏥 व्यावसायिक कचरा व स्वच्छता शुल्क (Commercial Waste Fee)',
      default_name_mr: 'व्यावसायिक कचरा व स्वच्छता सेवा शुल्क',
      code_prefix: 'WASTE_FEE_COMMERCIAL',
      account_class: 'service_charge',
      default_formula: 'fixed',
      desc: 'हॉटेल्स, पोल्ट्री, मंगल कार्यालय, दुकाने यांच्याकडून व्यावसायिक कचरा शुल्क',
    },
    {
      id: 'commercial_lighting_cess',
      label: '💡 व्यावसायिक दिवाबत्ती उपकर (Commercial Lighting Cess)',
      default_name_mr: 'व्यावसायिक दिवाबत्ती उपकर',
      code_prefix: 'LIGHT_TAX_COMMERCIAL',
      account_class: 'service_charge',
      default_formula: 'fixed',
      desc: 'व्यावसायिक आस्थापनांसाठी दिवाबत्ती विशेष उपकर',
    },
    {
      id: 'industrial_effluent_tax',
      label: '🏭 औद्योगिक सांडपाणी व प्रदूषण उपकर (Industrial Effluent Cess)',
      default_name_mr: 'औद्योगिक सांडपाणी व प्रदूषण उपकर',
      code_prefix: 'DRAINAGE_COMMERCIAL_TAX',
      account_class: 'service_charge',
      default_formula: 'fixed',
      desc: 'कारखाने व उद्योगांच्या सांडपाणी व मलनिस्सारण प्रक्रिया उपकर',
    },
    {
      id: 'other_business_cess',
      label: '⚖️ इतर व्यवसाय उपकर व फी (Other Business Cess)',
      default_name_mr: 'इतर विशेष व्यवसाय उपकर',
      code_prefix: 'SPECIAL_BIZ_CESS',
      account_class: 'service_charge',
      default_formula: 'fixed',
      desc: 'ग्रामसभा ठरावानुसार इतर व्यावसायिक उपकर किंवा फी',
    },
  ],
};

const PROPERTY_USAGE_OPTIONS = [
  { value: 'residential', label: 'निवासी / Residential' },
  { value: 'commercial', label: 'व्यावसायिक / Commercial' },
  { value: 'industrial', label: 'औद्योगिक / Industrial' },
  { value: 'agricultural', label: 'शेती / Agricultural' },
  { value: 'mixed', label: 'मिश्र वापर / Mixed' },
  { value: 'institutional', label: 'संस्थात्मक / Institutional' },
  { value: 'open_plot', label: 'मोकळा भूखंड / Open Plot' },
  { value: 'other', label: 'इतर / Other' },
];

const BUSINESS_TYPE_OPTIONS = BUSINESS_TYPES.map((type) => ({
  value: type,
  label: TYPE_LABELS[type] || type,
}));

export default function AdminTaxRuleFormScreen({
  mode = 'create',
  rule = null,
  defaultSubjectType = 'property',
  onCancel,
  onSubmit,
  busy = false,
}) {
  const isEdit = mode === 'edit';

  // 1. Target Domain: 'property' (Households / Properties) vs 'business' (Commercial)
  const [targetSubject, setTargetSubject] = useState(() => {
    if (rule?.subject_type === 'business' || defaultSubjectType === 'business') return 'business';
    return 'property';
  });

  // 2. Specific Category under Target Subject
  const availableCategories = TAX_CATEGORIES[targetSubject] || TAX_CATEGORIES.property;

  const [selectedCategoryId, setSelectedCategoryId] = useState(() => {
    if (rule?.code) {
      const match = availableCategories.find((c) => rule.code.startsWith(c.code_prefix.slice(0, 8)));
      if (match) return match.id;
    }
    return availableCategories[0].id;
  });

  const activeCategory = useMemo(
    () => availableCategories.find((c) => c.id === selectedCategoryId) || availableCategories[0],
    [availableCategories, selectedCategoryId]
  );

  // Form Fields
  const [nameMarathi, setNameMarathi] = useState(rule?.name_marathi || activeCategory.default_name_mr);
  const [nameEnglish, setNameEnglish] = useState(rule?.name || '');
  const [code, setCode] = useState(rule?.code || activeCategory.code_prefix);
  const [description, setDescription] = useState(rule?.description || '');

  // 3. Applicability & Eligibility State
  const [scopeMode, setScopeMode] = useState(() => {
    if (activeCategory.id.includes('water') || activeCategory.id.includes('drainage')) return 'filtered';
    return 'all';
  });
  const [selectedUsages, setSelectedUsages] = useState(() =>
    PROPERTY_USAGE_OPTIONS.map((o) => o.value)
  );
  const [waterConnectionFilter, setWaterConnectionFilter] = useState(
    activeCategory.id.includes('water') ? 'has_connection' : 'all'
  );
  const [lightingFilter, setLightingFilter] = useState(false);
  const [sanitaryFilter, setSanitaryFilter] = useState(false);
  const [selectedBizTypes, setSelectedBizTypes] = useState(() => [...BUSINESS_TYPES]);
  const [minTurnover, setMinTurnover] = useState('');

  // 4. Statutory Policy State
  const [effectiveFrom, setEffectiveFrom] = useState('1900-01-01');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [legalReference, setLegalReference] = useState(
    'महाराष्ट्र ग्रामपंचायत कर व फी नियम, १९६० (नियम २०(३))'
  );
  const [roundingMode, setRoundingMode] = useState('nearest_rupee');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [reason, setReason] = useState(
    isEdit ? 'कर सूत्राची माहिती अद्ययावत केली' : 'वार्षिक कर आकारणीसाठी नवीन कर सूत्र निश्चित केले'
  );

  // Handle Domain Switch (Property vs Business)
  const handleDomainChange = (newDomain) => {
    setTargetSubject(newDomain);
    const newCats = TAX_CATEGORIES[newDomain] || TAX_CATEGORIES.property;
    const firstCat = newCats[0];
    setSelectedCategoryId(firstCat.id);

    if (!isEdit) {
      setNameMarathi(firstCat.default_name_mr);
      setCode(firstCat.code_prefix);
      setScopeMode('all');
      setWaterConnectionFilter('all');
      setLightingFilter(false);
      setSanitaryFilter(false);
    }
  };

  // Handle Category Change inside current domain
  const handleCategoryChange = (e) => {
    const catId = e.target.value;
    setSelectedCategoryId(catId);
    const cat = availableCategories.find((c) => c.id === catId);
    if (cat && !isEdit) {
      setNameMarathi(cat.default_name_mr);
      setCode(cat.code_prefix);

      // Smart preset for applicability
      if (cat.id.includes('water')) {
        setScopeMode('filtered');
        setWaterConnectionFilter('has_connection');
      } else if (cat.id.includes('lighting')) {
        setScopeMode('filtered');
        setLightingFilter(true);
      } else if (cat.id.includes('sanitation') || cat.id.includes('drainage') || cat.id.includes('waste')) {
        setScopeMode('filtered');
        setSanitaryFilter(true);
      } else {
        setScopeMode('all');
        setWaterConnectionFilter('all');
        setLightingFilter(false);
        setSanitaryFilter(false);
      }
    }
  };

  const handleUsageToggle = (val) => {
    setSelectedUsages((prev) =>
      prev.includes(val) ? prev.filter((item) => item !== val) : [...prev, val]
    );
  };

  const handleBizTypeToggle = (val) => {
    setSelectedBizTypes((prev) =>
      prev.includes(val) ? prev.filter((item) => item !== val) : [...prev, val]
    );
  };

  // Compile Eligibility Config JSON
  const eligibilityConfig = useMemo(() => {
    if (scopeMode === 'all') return {};
    const conditions = [];

    if (targetSubject === 'property') {
      // Usage type filter
      if (selectedUsages.length > 0 && selectedUsages.length < PROPERTY_USAGE_OPTIONS.length) {
        conditions.push({
          field: 'usage_type',
          operator: selectedUsages.length === 1 ? 'equals' : 'in',
          value: selectedUsages.length === 1 ? selectedUsages[0] : selectedUsages,
        });
      }
      // Water connection filter
      if (waterConnectionFilter === 'has_connection') {
        conditions.push({
          field: 'water_tax_type',
          operator: 'not_equals',
          value: 'none',
        });
      }
      // Lighting filter
      if (lightingFilter) {
        conditions.push({
          field: 'lighting_tax_applicable',
          operator: 'equals',
          value: true,
        });
      }
      // Sanitary filter
      if (sanitaryFilter) {
        conditions.push({
          field: 'sanitary_tax_applicable',
          operator: 'equals',
          value: true,
        });
      }
    }

    if (targetSubject === 'business') {
      // Business trade types
      if (selectedBizTypes.length > 0 && selectedBizTypes.length < BUSINESS_TYPE_OPTIONS.length) {
        conditions.push({
          field: 'business_type',
          operator: selectedBizTypes.length === 1 ? 'equals' : 'in',
          value: selectedBizTypes.length === 1 ? selectedBizTypes[0] : selectedBizTypes,
        });
      }
      // Turnover threshold
      if (minTurnover && Number(minTurnover) > 0) {
        conditions.push({
          field: 'annual_turnover',
          operator: 'gte',
          value: Number(minTurnover),
        });
      }
    }

    if (conditions.length === 0) return {};
    if (conditions.length === 1) return conditions[0];
    return { all: conditions };
  }, [
    scopeMode,
    targetSubject,
    selectedUsages,
    waterConnectionFilter,
    lightingFilter,
    sanitaryFilter,
    selectedBizTypes,
    minTurnover,
  ]);

  // Dynamic Eligibility Explanation
  const eligibilityExplanation = useMemo(() => {
    if (scopeMode === 'all') {
      return targetSubject === 'business'
        ? 'सर्व नोंदणीकृत व्यवसायांना हा कर सरसकट लागू होईल.'
        : 'ग्रामपंचायतीमधील सर्व मालमत्ता/घरांना हा कर सरसकट लागू होईल.';
    }
    const parts = [];
    if (targetSubject === 'property') {
      if (selectedUsages.length > 0 && selectedUsages.length < PROPERTY_USAGE_OPTIONS.length) {
        const labels = selectedUsages
          .map((u) => PROPERTY_USAGE_OPTIONS.find((o) => o.value === u)?.label.split(' ')[0])
          .join(', ');
        parts.push(`वापर प्रकार: ${labels}`);
      }
      if (waterConnectionFilter === 'has_connection') {
        parts.push('फक्त नळ पाणी जोडणी असणाऱ्या मालमत्ता');
      }
      if (lightingFilter) {
        parts.push('फक्त दिवाबत्ती सेवा लागू असलेल्या मालमत्ता');
      }
      if (sanitaryFilter) {
        parts.push('फक्त स्वच्छता सेवा लागू असलेल्या मालमत्ता');
      }
    } else {
      if (selectedBizTypes.length > 0 && selectedBizTypes.length < BUSINESS_TYPE_OPTIONS.length) {
        parts.push(`${selectedBizTypes.length} निवडक व्यवसाय प्रकार`);
      }
      if (minTurnover && Number(minTurnover) > 0) {
        parts.push(`₹${Number(minTurnover).toLocaleString('en-IN')} पेक्षा जास्त उलाढाल असणारे व्यवसाय`);
      }
    }
    return parts.length > 0
      ? `लागू अटी: ${parts.join(' + ')}`
      : 'सर्व खात्यांना लागू होईल.';
  }, [
    scopeMode,
    targetSubject,
    selectedUsages,
    waterConnectionFilter,
    lightingFilter,
    sanitaryFilter,
    selectedBizTypes,
    minTurnover,
  ]);

  return (
    <div className="atx-rule-form-screen">
      {/* Top Header Bar */}
      <header className="atx-form-top-nav">
        <div className="atx-form-heading-info">
          <h2>{isEdit ? 'कर सूत्र माहिती संपादित करा' : 'नवीन कर सूत्र व आकारणी रचना'}</h2>
          <span>{isEdit ? '(विद्यमान सूत्र अद्ययावत करत आहात)' : '(मालमत्ता व व्यवसायांसाठी स्वतंत्र सेवा नियम)'}</span>
        </div>
        <button type="button" className="atx-form-back-btn" onClick={onCancel}>
          ← कर सूत्रे सूचीकडे परत जा
        </button>
      </header>

      <form onSubmit={onSubmit} className="atx-form-container">
        {/* Hidden inputs to pass data cleanly to form handler */}
        <input type="hidden" name="subject_type" value={targetSubject} />
        <input type="hidden" name="account_class" value={activeCategory.account_class} />
        <input type="hidden" name="billing_frequency" value="annual" />
        <input type="hidden" name="finance_category" value="tax_revenue" />
        <input type="hidden" name="eligibility_config" value={JSON.stringify(eligibilityConfig)} />

        {/* CARD 1: Tax Domain & Service Classification (Clean Form Inputs) */}
        <section className="atx-card-compact">
          <div className="atx-card-title-row">
            <h3>
              <span className="atx-step-dot">१</span>
              कर वर्गवारी व सेवा प्रकार (Tax Domain & Service Classification)
            </h3>
            <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
              घरगुती मालमत्ता व व्यावसायिक आस्थापनांसाठी स्वतंत्र दर व सेवा नियम
            </span>
          </div>

          {/* Form Row 1: Target Entity Dropdown & Category Dropdown */}
          <div className="atx-input-row-2">
            <div className="atx-form-item">
              <label htmlFor="target_domain_select">
                <span>
                  कर कोणासाठी लागू आहे? <span className="atx-label-sub">(Target Domain)</span>{' '}
                  <span className="atx-req-star">*</span>
                </span>
              </label>
              <select
                id="target_domain_select"
                value={targetSubject}
                onChange={(e) => handleDomainChange(e.target.value)}
                disabled={isEdit}
              >
                <option value="property">🏠 मालमत्ता व घरे (Properties / Households)</option>
                <option value="business">🏢 दुकाने व व्यवसाय (Commercial Businesses)</option>
              </select>
              <span className="atx-item-hint">
                {targetSubject === 'property'
                  ? 'घरे, इमारती, गाळे व रहिवासी पाणीपट्टी/स्वच्छतेसाठी नियम.'
                  : 'व्यापारी दुकाने, हॉटेल्स, कारखाने व उद्योग आस्थापनांसाठी स्वतंत्र नियम.'}
              </span>
            </div>

            <div className="atx-form-item">
              <label htmlFor="category_select">
                <span>
                  कर / सेवा प्रकार <span className="atx-label-sub">(Tax / Service Category)</span>{' '}
                  <span className="atx-req-star">*</span>
                </span>
              </label>
              <select
                id="category_select"
                value={selectedCategoryId}
                onChange={handleCategoryChange}
                disabled={isEdit}
              >
                {availableCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.label}
                  </option>
                ))}
              </select>
              <span className="atx-item-hint">{activeCategory.desc}</span>
            </div>
          </div>

          {/* Form Row 2: Marathi Name & Rule Code */}
          <div className="atx-input-row-2">
            <div className="atx-form-item">
              <label htmlFor="name_marathi">
                <span>
                  नागरिकांना दिसणारे मराठी नाव <span className="atx-req-star">*</span>
                </span>
              </label>
              <input
                id="name_marathi"
                name="name_marathi"
                required
                value={nameMarathi}
                onChange={(e) => setNameMarathi(e.target.value)}
                placeholder="उदा. इमारत व बिगरशेती जमीन कर / घरगुती पाणीपट्टी"
              />
              <span className="atx-item-hint">बिल, पावती व कर खात्यावर हे नाव स्पष्टपणे नोंदवले जाईल.</span>
            </div>

            <div className="atx-form-item">
              <label htmlFor="rule_code">
                <span>
                  सूत्र संकेतांक <span className="atx-label-sub">(Rule Code)</span>{' '}
                  <span className="atx-req-star">*</span>
                </span>
              </label>
              <input
                id="rule_code"
                name="code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="उदा. PROP_TAX_GENERAL"
                readOnly={isEdit}
              />
              <span className="atx-item-hint">
                {isEdit ? 'संकेतांक बदलता येत नाही.' : 'प्रणालीतील अद्वितीय ओळख (इंग्रजीत).'}
              </span>
            </div>
          </div>

          {/* Form Row 3: English Name & Frequency */}
          <div className="atx-input-row-2">
            <div className="atx-form-item">
              <label htmlFor="name_english">
                <span>
                  प्रशासकीय इंग्रजी नाव <span className="atx-label-sub">(English Name)</span>
                </span>
              </label>
              <input
                id="name_english"
                name="name"
                value={nameEnglish}
                onChange={(e) => setNameEnglish(e.target.value)}
                placeholder="उदा. General Property Tax / Domestic Water Charges"
              />
            </div>

            <div className="atx-form-item">
              <label htmlFor="billing_freq">
                <span>
                  आकारणी वारंवारता <span className="atx-label-sub">(Billing Frequency)</span>
                </span>
              </label>
              <input id="billing_freq" value="वार्षिक (Annual - कायदेशीर नियम)" readOnly aria-readonly="true" />
            </div>
          </div>

          <div className="atx-form-item">
            <label htmlFor="rule_desc">
              <span>
                थोडक्यात माहिती / वर्णन <span className="atx-label-sub">(Description - ऐच्छिक)</span>
              </span>
            </label>
            <textarea
              id="rule_desc"
              name="description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="या कर सूत्राचा उद्देश, ठराव संदर्भ किंवा अतिरिक्त प्रशासकीय माहिती..."
            />
          </div>
        </section>

        {/* CARD 2: Pricing Formula & Rates via TaxPricingRuleEditor (Only on Create) */}
        {!isEdit && (
          <section className="atx-card-compact">
            <div className="atx-card-title-row">
              <h3>
                <span className="atx-step-dot">२</span>
                कर आकारणी पद्धत व दर (६ पद्धती उपलब्ध)
              </h3>
              <span style={{ fontSize: '0.82rem', color: '#0f766e', fontWeight: 750 }}>
                ✓ निश्चित दर, बांधकाम वर्गवारी, टक्केवारी, क्षेत्रफळ, टप्पे व गणिती सूत्र
              </span>
            </div>

            {/* Embedded Full 6 Pricing Rules with Form Dropdown Input */}
            <TaxPricingRuleEditor
              key={`pricing-${activeCategory.id}-${targetSubject}`}
              initialType={activeCategory.default_formula}
              subjectType={targetSubject}
              initialConfig={
                activeCategory.default_formula === 'property_class_rate'
                  ? {
                      base_field: 'assessed_value',
                      class_field: 'construction_type',
                      rate_mode: 'per_100',
                      rates: { kachha: 0.2, semi_paka: 0.35, load_bearing: 0.5, rcc: 0.75, open_plot: 0.25 },
                    }
                  : { amount: 500 }
              }
            />
          </section>
        )}

        {/* CARD 3: Applicability & Eligibility Rules (Only on Create) */}
        {!isEdit && (
          <section className="atx-card-compact">
            <div className="atx-card-title-row">
              <h3>
                <span className="atx-step-dot">३</span>
                कर लागू होण्याचे निकष (Applicability & Eligibility Rules)
              </h3>
              <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                प्रत्येक मालमत्ता किंवा व्यवसायावर योग्य कर आकारणी होण्यासाठी निकष निवडा
              </span>
            </div>

            <div className="atx-eligibility-panel">
              <div className="atx-radio-inline-row">
                <label className={`atx-radio-pill ${scopeMode === 'all' ? 'checked' : ''}`}>
                  <input
                    type="radio"
                    name="scope_mode_radio"
                    value="all"
                    checked={scopeMode === 'all'}
                    onChange={() => setScopeMode('all')}
                  />
                  <span>🟢 सर्व खात्यांना सरसकट लागू (Applicable to All)</span>
                </label>

                <label className={`atx-radio-pill ${scopeMode === 'filtered' ? 'checked' : ''}`}>
                  <input
                    type="radio"
                    name="scope_mode_radio"
                    value="filtered"
                    checked={scopeMode === 'filtered'}
                    onChange={() => setScopeMode('filtered')}
                  />
                  <span>🎯 विशिष्ट निकषांनुसार लागू (Filter by Conditions)</span>
                </label>
              </div>

              {scopeMode === 'filtered' && (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {targetSubject === 'property' && (
                    <>
                      {/* Property Usage Filter */}
                      <div className="atx-filter-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span className="atx-filter-title">१. मालमत्ता वापराचे स्वरूप (Property Usage):</span>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              type="button"
                              className="atx-mini-action-btn"
                              onClick={() => setSelectedUsages(PROPERTY_USAGE_OPTIONS.map((o) => o.value))}
                            >
                              ✓ सर्व निवडा
                            </button>
                            <button
                              type="button"
                              className="atx-mini-action-btn"
                              onClick={() => setSelectedUsages([])}
                            >
                              ✕ सर्व काढा
                            </button>
                          </div>
                        </div>
                        <div className="atx-checkbox-grid">
                          {PROPERTY_USAGE_OPTIONS.map((opt) => (
                            <label
                              key={opt.value}
                              className={`atx-checkbox-pill ${selectedUsages.includes(opt.value) ? 'checked' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedUsages.includes(opt.value)}
                                onChange={() => handleUsageToggle(opt.value)}
                              />
                              <span>{opt.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Water Connection Filter */}
                      <div className="atx-filter-group">
                        <span className="atx-filter-title">२. नळ पाणी जोडणी अट (Water Connection Filter):</span>
                        <div className="atx-checkbox-grid">
                          <label
                            className={`atx-checkbox-pill ${waterConnectionFilter === 'all' ? 'checked' : ''}`}
                          >
                            <input
                              type="radio"
                              name="water_filter_radio"
                              checked={waterConnectionFilter === 'all'}
                              onChange={() => setWaterConnectionFilter('all')}
                            />
                            <span>पाणी जोडणी असो वा नसो, सर्व मालमत्तांना लागू</span>
                          </label>

                          <label
                            className={`atx-checkbox-pill ${waterConnectionFilter === 'has_connection' ? 'checked' : ''}`}
                          >
                            <input
                              type="radio"
                              name="water_filter_radio"
                              checked={waterConnectionFilter === 'has_connection'}
                              onChange={() => setWaterConnectionFilter('has_connection')}
                            />
                            <span>फक्त सक्रिय नळ जोडणी असणाऱ्या मालमत्तांनाच लागू (Piped Water Connection Only)</span>
                          </label>
                        </div>
                      </div>

                      {/* Service Specific Flags */}
                      <div className="atx-filter-group">
                        <span className="atx-filter-title">३. विशेष सेवा कर अट (Special Service Flags):</span>
                        <div className="atx-checkbox-grid">
                          <label className={`atx-checkbox-pill ${lightingFilter ? 'checked' : ''}`}>
                            <input
                              type="checkbox"
                              checked={lightingFilter}
                              onChange={(e) => setLightingFilter(e.target.checked)}
                            />
                            <span>फक्त ज्या मालमत्तांवर दिवाबत्ती कर लागू चिन्हांकित आहे (Lighting Applicable)</span>
                          </label>

                          <label className={`atx-checkbox-pill ${sanitaryFilter ? 'checked' : ''}`}>
                            <input
                              type="checkbox"
                              checked={sanitaryFilter}
                              onChange={(e) => setSanitaryFilter(e.target.checked)}
                            />
                            <span>फक्त ज्या मालमत्तांवर स्वच्छता/गटार कर लागू चिन्हांकित आहे (Sanitary Applicable)</span>
                          </label>
                        </div>
                      </div>
                    </>
                  )}

                  {targetSubject === 'business' && (
                    <>
                      {/* Business Trade Types */}
                      <div className="atx-filter-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span className="atx-filter-title">१. व्यवसाय प्रकार (Business Types):</span>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              type="button"
                              className="atx-mini-action-btn"
                              onClick={() => setSelectedBizTypes([...BUSINESS_TYPES])}
                            >
                              ✓ सर्व निवडा
                            </button>
                            <button
                              type="button"
                              className="atx-mini-action-btn"
                              onClick={() => setSelectedBizTypes([])}
                            >
                              ✕ सर्व काढा
                            </button>
                          </div>
                        </div>
                        <div className="atx-checkbox-grid">
                          {BUSINESS_TYPE_OPTIONS.map((opt) => (
                            <label
                              key={opt.value}
                              className={`atx-checkbox-pill ${selectedBizTypes.includes(opt.value) ? 'checked' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedBizTypes.includes(opt.value)}
                                onChange={() => handleBizTypeToggle(opt.value)}
                              />
                              <span>{opt.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Minimum Turnover Threshold */}
                      <div className="atx-filter-group">
                        <span className="atx-filter-title">२. वार्षिक उलाढाल अट (Turnover Threshold):</span>
                        <div className="atx-input-row-2">
                          <div className="atx-form-item">
                            <label>किमान वार्षिक उलाढाल (₹)</label>
                            <input
                              type="number"
                              min="0"
                              value={minTurnover}
                              onChange={(e) => setMinTurnover(e.target.value)}
                              placeholder="उदा. 100000 (यापेक्षा जास्त उलाढाल असणाऱ्या व्यवसायांनाच लागू)"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Dynamic Summary Explanation Box */}
              <div className="atx-eligibility-summary-box">
                <span style={{ fontSize: '1rem' }}>✓</span>
                <span>{eligibilityExplanation}</span>
              </div>
            </div>
          </section>
        )}

        {/* CARD 4: Statutory Policy & Administrative Limits */}
        {!isEdit && (
          <section className="atx-card-compact">
            <div className="atx-card-title-row">
              <h3>
                <span className="atx-step-dot">४</span>
                वैधानिक संदर्भ व धोरण (Statutory Policy & Limits)
              </h3>
            </div>

            <div className="atx-input-row-3">
              <div className="atx-form-item">
                <label htmlFor="eff_from">
                  <span>
                    पासून लागू <span className="atx-label-sub">(Effective From)</span>{' '}
                    <span className="atx-req-star">*</span>
                  </span>
                </label>
                <input
                  id="eff_from"
                  name="effective_from"
                  type="date"
                  required
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                />
              </div>

              <div className="atx-form-item">
                <label htmlFor="eff_to">
                  <span>
                    पर्यंत लागू <span className="atx-label-sub">(Effective To - ऐच्छिक)</span>
                  </span>
                </label>
                <input
                  id="eff_to"
                  name="effective_to"
                  type="date"
                  value={effectiveTo}
                  onChange={(e) => setEffectiveTo(e.target.value)}
                />
              </div>

              <div className="atx-form-item">
                <label htmlFor="rnd_mode">
                  <span>
                    पूर्णांकन पद्धत <span className="atx-label-sub">(Rounding Mode)</span>
                  </span>
                </label>
                <select
                  id="rnd_mode"
                  name="rounding_mode"
                  value={roundingMode}
                  onChange={(e) => setRoundingMode(e.target.value)}
                >
                  <option value="nearest_rupee">जवळच्या रुपयात (Nearest Rupee)</option>
                  <option value="up_rupee">पुढील रुपयात (Round Up)</option>
                  <option value="down_rupee">मागील रुपयात (Round Down)</option>
                  <option value="none">पूर्णांकन नाही (Exact)</option>
                </select>
              </div>
            </div>

            <div className="atx-input-row-3">
              <div className="atx-form-item">
                <label htmlFor="min_amt">
                  <span>
                    किमान आकारणी रक्कम <span className="atx-label-sub">(₹ Min Amount)</span>
                  </span>
                </label>
                <input
                  id="min_amt"
                  name="minimum_amount"
                  type="number"
                  min="0"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  placeholder="उदा. 50"
                />
              </div>

              <div className="atx-form-item">
                <label htmlFor="max_amt">
                  <span>
                    कमाल आकारणी रक्कम <span className="atx-label-sub">(₹ Max Amount)</span>
                  </span>
                </label>
                <input
                  id="max_amt"
                  name="maximum_amount"
                  type="number"
                  min="0"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  placeholder="अमर्याद असल्यास रिकामे ठेवा"
                />
              </div>

              <div className="atx-form-item">
                <label htmlFor="leg_ref">
                  <span>
                    कायदेशीर संदर्भ <span className="atx-label-sub">(Legal Reference)</span>{' '}
                    <span className="atx-req-star">*</span>
                  </span>
                </label>
                <input
                  id="leg_ref"
                  name="legal_reference"
                  required
                  value={legalReference}
                  onChange={(e) => setLegalReference(e.target.value)}
                />
              </div>
            </div>

            <div className="atx-form-item">
              <label htmlFor="adm_reason">
                <span>
                  प्रशासकीय कारण / शेरा <span className="atx-label-sub">(Remarks)</span>{' '}
                  <span className="atx-req-star">*</span>
                </span>
              </label>
              <input
                id="adm_reason"
                name="reason"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          </section>
        )}

        {isEdit && (
          <section className="atx-card-compact">
            <div className="atx-form-item">
              <label htmlFor="edit_adm_reason">
                <span>
                  दुरुस्तीचे कारण / शेरा <span className="atx-label-sub">(Remarks)</span>{' '}
                  <span className="atx-req-star">*</span>
                </span>
              </label>
              <input
                id="edit_adm_reason"
                name="reason"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          </section>
        )}

        {/* Sticky Action Bar */}
        <div className="atx-form-action-bar">
          <button type="button" className="atx-btn-cancel-main" onClick={onCancel} disabled={busy}>
            रद्द करा व मागे जा
          </button>
          <button type="submit" className="atx-btn-submit-main" disabled={busy}>
            {busy ? 'जतन होत आहे…' : isEdit ? '✓ कर सूत्र माहिती अद्ययावत करा' : '✓ कर सूत्र व नियम जतन करा'}
          </button>
        </div>
      </form>
    </div>
  );
}
